import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  listTrails,
  ApiError,
  type TrailListItem,
  type TrailDetail,
} from "../api";
import { TrailDrawer } from "./TrailDrawer";
import { AddTrailDrawer } from "./AddTrailDrawer";
import { SAVED_VIEWS, SavedViewSidebar } from "./SavedViewSidebar";
import { BatchActionBar, type BatchOutcome } from "./BatchActionBar";
import { ACTIVITIES, type ActivityKey } from "../activities";

/**
 * Curator catalog workspace — Phase 2.
 *
 * Now loads the full catalog up-front (cursor-walked internally) so
 * search, state filter, and saved views all resolve instantly against
 * an in-memory snapshot. List is virtualized via @tanstack/react-virtual
 * — only the visible window of rows is mounted to the DOM, which keeps
 * a 2,000-trail catalog buttery on a phone.
 *
 * The "Load more" button from Phase 1 is gone; if you can search the
 * whole catalog, you don't need to paginate. We'll switch to
 * server-side facets once the catalog crosses ~5k rows; until then the
 * UX wins from instant-everything outweigh the up-front fetch.
 *
 * The legacy CatalogManager is still reachable via ?legacy=1.
 */

const ROW_HEIGHT = 60; // px — comfortable touch target, balanced row density
const PAGE_LIMIT = 200; // matches the worker's MAX_LIMIT-friendly chunk size

export function CatalogWorkspace() {
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [activeView, setActiveView] = useState<string>("all");
  /** Single-select activity filter (Hick's Law: one discipline at a
   *  time keeps the chip strip decisive). Null = no filter applied.
   *  Confirmed multi-select on the editor / OR-semantics in the
   *  filter were rejected in the planning conversation. */
  const [activityFilter, setActivityFilter] = useState<ActivityKey | null>(null);

  /** Full in-memory snapshot of the catalog. null = still loading the
   *  first page. Subsequent pages append in place. */
  const [items, setItems] = useState<TrailListItem[] | null>(null);
  const [loadedCount, setLoadedCount] = useState(0);
  const [totalEstimate, setTotalEstimate] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [addingTrail, setAddingTrail] = useState(false);
  // After a brand-new trail saves, hand the curator a one-line note so
  // they understand whether it was inserted or matched an existing
  // row (proposeTrail can dedupe on name + status URL).
  const [createdNote, setCreatedNote] = useState<string | null>(null);

  // Multi-select state — Phase 3. When `selectMode` is on, row taps
  // toggle selection instead of opening the drawer. Selected lives as
  // a Set for O(1) toggle/lookup at scale. We clear on view change so
  // a curator never has selections hiding in a view they're not
  // currently looking at (per the design note in the audit).
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const listRef = useRef<HTMLDivElement | null>(null);

  // ---- Initial load: walk every page via cursor ----------------------
  // We load eagerly so filters/views are instant. The endpoint is fast
  // (worker → D1 LIKE scan) and the payload per row is tiny.
  useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      setLoading(true);
      setError(null);
      try {
        const all: TrailListItem[] = [];
        let cursor: string | undefined;
        do {
          const page = await listTrails({ cursor, limit: PAGE_LIMIT });
          if (cancelled) return;
          all.push(...page.items);
          setLoadedCount(all.length);
          // Optimistic total guess: if there's another page, we know
          // there's at least PAGE_LIMIT more rows; otherwise we know
          // exactly. Useful for the "Loading 437 trails…" line.
          setTotalEstimate(page.nextCursor ? all.length + PAGE_LIMIT : all.length);
          cursor = page.nextCursor ?? undefined;
        } while (cursor && !cancelled);

        if (!cancelled) {
          setItems(all);
          setTotalEstimate(all.length);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? err.message : "Couldn't load the catalog."
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Compute per-view counts off the loaded snapshot ---------------
  // Recomputed only when the snapshot changes; cheap at any reasonable
  // catalog size.
  const viewCounts = useMemo(() => {
    if (!items) return {} as Record<string, number>;
    const out: Record<string, number> = {};
    for (const v of SAVED_VIEWS) {
      out[v.id] = items.reduce((n, t) => (v.predicate(t) ? n + 1 : n), 0);
    }
    return out;
  }, [items]);

  // ---- Apply view + search + state filter to the snapshot -----------
  const filtered = useMemo(() => {
    if (!items) return [] as TrailListItem[];
    const view = SAVED_VIEWS.find((v) => v.id === activeView) ?? SAVED_VIEWS[0];
    const needle = q.trim().toLowerCase();
    const stateNeedle = stateFilter.trim().toUpperCase();
    return items.filter((t) => {
      if (!view.predicate(t)) return false;
      if (stateNeedle && (t.state ?? "").toUpperCase() !== stateNeedle)
        return false;
      if (activityFilter) {
        const tags = (t.activities ?? []) as string[];
        if (!tags.includes(activityFilter)) return false;
      }
      if (needle) {
        const hay = `${t.name} ${t.region ?? ""} ${t.state ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, activeView, q, stateFilter, activityFilter]);

  // ---- Virtualizer ---------------------------------------------------
  const virtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  function applyUpdated(updated: TrailDetail) {
    setItems((cur) =>
      cur ? cur.map((row) => (row.slug === updated.slug ? updated : row)) : cur
    );
  }

  // After AddTrailDrawer saves: stash the new row in the snapshot (if
  // brand new) or just open it (if deduped), and chain straight into
  // the regular edit drawer so the curator can keep going without
  // re-finding the trail in the list. Peak-End rule: ending the create
  // flow on a populated drawer feels great.
  function handleCreatedTrail(trail: TrailDetail, deduped: boolean) {
    setItems((cur) => {
      if (!cur) return cur;
      const exists = cur.some((r) => r.slug === trail.slug);
      if (exists) {
        return cur.map((r) => (r.slug === trail.slug ? trail : r));
      }
      // Insert at the top so the curator immediately sees their work.
      return [trail, ...cur];
    });
    setAddingTrail(false);
    setActiveSlug(trail.slug);
    setCreatedNote(
      deduped
        ? `"${trail.name}" already existed in the catalog — opened it for you.`
        : `Added "${trail.name}". Keep going to add a pin or crew.`
    );
    // Note auto-fades after a few seconds so it doesn't linger.
    setTimeout(() => setCreatedNote(null), 6000);
  }

  function toggleRow(slug: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  function enterSelectMode() {
    setSelectMode(true);
    setSelected(new Set());
  }

  function exitSelectMode() {
    setSelectMode(false);
    setSelected(new Set());
  }

  function selectAllInView() {
    setSelected(new Set(filtered.map((t) => t.slug)));
  }

  // Apply batch outcomes back into the in-memory snapshot:
  //   verify   → patch verifiedAt to now
  //   unverify → patch verifiedAt to null
  //   delete   → drop the row
  // Failures are surfaced via an inline note; the row stays put so
  // the curator can retry without re-selecting.
  function handleBatchComplete(outcomes: BatchOutcome[]) {
    const now = Math.floor(Date.now() / 1000);
    setItems((cur) => {
      if (!cur) return cur;
      const successDelete = new Set(
        outcomes.filter((o) => o.kind === "delete" && o.ok).map((o) => o.slug)
      );
      const successVerify = new Set(
        outcomes.filter((o) => o.kind === "verify" && o.ok).map((o) => o.slug)
      );
      const successUnverify = new Set(
        outcomes.filter((o) => o.kind === "unverify" && o.ok).map((o) => o.slug)
      );
      return cur
        .filter((row) => !successDelete.has(row.slug))
        .map((row) => {
          if (successVerify.has(row.slug)) {
            return { ...row, verifiedAt: now };
          }
          if (successUnverify.has(row.slug)) {
            return { ...row, verifiedAt: null };
          }
          return row;
        });
    });
    const failures = outcomes.filter((o) => !o.ok);
    if (failures.length > 0) {
      setError(
        `${failures.length} of ${outcomes.length} actions failed. First error: ${failures[0].error}`
      );
    } else {
      setError(null);
    }
    exitSelectMode();
  }

  // Clear selections any time the underlying filter changes — keeps
  // curators from triggering a batch action against rows they can't
  // currently see.
  useEffect(() => {
    if (selected.size > 0) setSelected(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, q, stateFilter]);

  const total = items?.length ?? 0;

  return (
    <section className="catalog-workspace card">
      <header className="catalog-workspace-head">
        <div>
          <h3 style={{ margin: 0 }}>Catalog</h3>
          <p className="muted" style={{ margin: "4px 0 0" }}>
            {selectMode
              ? "Tap rows to pick them. Use the bar at the bottom to act on the group."
              : "Tap any trail to open the trailhead drawer — edit details and drop a pin in one place."}
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {selectMode ? (
            <>
              <button
                type="button"
                className="small secondary"
                onClick={selectAllInView}
                disabled={filtered.length === 0}
              >
                Select all in view
              </button>
              <button
                type="button"
                className="small secondary"
                onClick={exitSelectMode}
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="small secondary"
                onClick={enterSelectMode}
                disabled={items === null || items.length === 0}
              >
                Select
              </button>
              <button
                type="button"
                className="small"
                onClick={() => setAddingTrail(true)}
              >
                + Add a trail
              </button>
            </>
          )}
        </div>
      </header>

      {createdNote && (
        <div className="created-banner" role="status">{createdNote}</div>
      )}

      <div className="catalog-workspace-grid">
        <SavedViewSidebar
          active={activeView}
          counts={viewCounts}
          onSelect={setActiveView}
        />

        <div className="catalog-workspace-main">
          <div className="catalog-workspace-filters">
            <input
              type="search"
              placeholder="Search by name or city"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <input
              type="text"
              placeholder="State"
              value={stateFilter}
              maxLength={4}
              onChange={(e) => setStateFilter(e.target.value.toUpperCase())}
              style={{ maxWidth: 96 }}
            />
            {(q || stateFilter || activityFilter) && (
              <button
                type="button"
                className="secondary"
                onClick={() => {
                  setQ("");
                  setStateFilter("");
                  setActivityFilter(null);
                }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Activity filter chips — single-select. Tapping the same
              chip again clears the filter (no need for a separate
              "all" pill). Sits directly under the search inputs so
              the curator's eye reads "search → narrow by discipline
              → list" top-to-bottom. */}
          <div className="activity-filter-strip" role="group" aria-label="Filter by activity">
            {ACTIVITIES.map((a) => {
              const active = activityFilter === a.key;
              return (
                <button
                  key={a.key}
                  type="button"
                  className={`activity-filter-chip ${active ? "on" : ""}`}
                  onClick={() =>
                    setActivityFilter(active ? null : a.key)
                  }
                  aria-pressed={active}
                >
                  <span className="activity-filter-chip-icon">{a.icon}</span>
                  <span>{a.label}</span>
                </button>
              );
            })}
          </div>

          {error && (
            <div className="error" style={{ margin: "8px 0" }}>
              {error}
            </div>
          )}

          <div className="catalog-workspace-meta muted">
            {loading && items === null ? (
              <span>
                Hitting the trail… loaded {loadedCount}
                {totalEstimate ? ` / ~${totalEstimate}` : ""}
              </span>
            ) : (
              <span>
                Showing {filtered.length}
                {filtered.length !== total ? ` of ${total}` : ""} trail
                {total === 1 ? "" : "s"}
              </span>
            )}
          </div>

          <div
            ref={listRef}
            className="catalog-workspace-virtual"
            style={{ height: `calc(100vh - 360px)` }}
            role="list"
          >
            {/* First-load skeleton — keeps vertical rhythm so the
                layout doesn't shift when real rows arrive. Matches
                ROW_HEIGHT so the virtualizer takes over without a
                visible jump. */}
            {items === null && (
              <>
                {Array.from({ length: 10 }).map((_, i) => (
                  <SkeletonRow key={i} />
                ))}
              </>
            )}

            {items !== null && filtered.length === 0 && !loading && (
              <div className="empty" style={{ padding: "40px 0" }}>
                {items.length === 0
                  ? "No trails in the catalog yet."
                  : "Nothing matches this line. Widen your search or pick another view."}
              </div>
            )}

            {filtered.length > 0 && (
              <div
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((vRow) => {
                  const row = filtered[vRow.index];
                  const isSelected = selected.has(row.slug);
                  return (
                    <div
                      key={row.slug}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        transform: `translateY(${vRow.start}px)`,
                        height: `${vRow.size}px`,
                      }}
                    >
                      <button
                        type="button"
                        className={`catalog-workspace-row ${isSelected ? "selected" : ""}`}
                        onClick={() =>
                          selectMode
                            ? toggleRow(row.slug)
                            : setActiveSlug(row.slug)
                        }
                        aria-pressed={selectMode ? isSelected : undefined}
                      >
                        {selectMode && (
                          <span
                            className={`row-checkbox ${isSelected ? "checked" : ""}`}
                            aria-hidden="true"
                          >
                            {isSelected ? "✓" : ""}
                          </span>
                        )}
                        <div className="catalog-workspace-row-main">
                          <div className="catalog-workspace-row-name">
                            {row.name}
                          </div>
                          <div className="catalog-workspace-row-sub muted">
                            {[row.region, row.state]
                              .filter(Boolean)
                              .join(", ") || "Needs a pin"}
                            {row.organization && (
                              <>
                                <span className="catalog-row-divider"> · </span>
                                <span className="catalog-row-org-chip">
                                  {row.organization.logoUrl ? (
                                    <img
                                      src={row.organization.logoUrl}
                                      alt=""
                                      className="catalog-row-org-logo"
                                    />
                                  ) : (
                                    <span
                                      className="catalog-row-org-logo placeholder"
                                      aria-hidden="true"
                                    >
                                      🏔
                                    </span>
                                  )}
                                  <span>{row.organization.name}</span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="catalog-workspace-row-meta">
                          <StatusPill status={row.status.value} />
                          {row.verifiedAt != null && (
                            <span className="verified-chip" title="Verified">
                              ✓
                            </span>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {activeSlug && (
        <TrailDrawer
          slug={activeSlug}
          onClose={() => setActiveSlug(null)}
          onSaved={applyUpdated}
          onDeleted={(deletedSlug) => {
            setItems((cur) =>
              cur ? cur.filter((row) => row.slug !== deletedSlug) : cur
            );
            setActiveSlug(null);
          }}
        />
      )}

      {selectMode && selected.size > 0 && (
        <BatchActionBar
          selectedSlugs={Array.from(selected)}
          onCancel={exitSelectMode}
          onComplete={handleBatchComplete}
        />
      )}

      {addingTrail && (
        <AddTrailDrawer
          onClose={() => setAddingTrail(false)}
          onCreated={handleCreatedTrail}
        />
      )}
    </section>
  );
}

function SkeletonRow() {
  return (
    <div className="catalog-workspace-skeleton" aria-hidden="true">
      <div style={{ flex: 1, minWidth: 0 }}>
        <span className="skeleton-bar skeleton-name" />
        <span className="skeleton-bar skeleton-sub" />
      </div>
      <span className="skeleton-bar skeleton-pill" />
    </div>
  );
}

function StatusPill({
  status,
}: {
  status: "open" | "closed" | "partially_open" | "unknown" | null;
}) {
  const label =
    status === "open"
      ? "Open"
      : status === "closed"
        ? "Closed"
        : status === "partially_open"
          ? "Caution"
          : "Unknown";
  const tone =
    status === "open"
      ? "open"
      : status === "closed"
        ? "closed"
        : status === "partially_open"
          ? "caution"
          : "unknown";
  return <span className={`status-pill status-pill-${tone}`}>{label}</span>;
}
